import { useEffect, useState, useCallback } from 'react'
import {
  nudgeSupported, isNudgeEnabled, scheduleNudge, disableNudge, syncNudge, NUDGE_HOUR,
} from '@/lib/nudge'

/**
 * The daily morning nudge, as a preference.
 *
 * Per device rather than per account, because the schedule lives on the device.
 * Someone signed in on a phone and a laptop should get the phone nudge without
 * the laptop trying to fire one it cannot deliver.
 */
export function useNudge() {
  const supported = nudgeSupported()
  const [enabled, setEnabled] = useState(false)
  const [busy, setBusy] = useState(false)
  const [blocked, setBlocked] = useState(false)

  useEffect(() => {
    if (!supported) return
    void isNudgeEnabled().then(setEnabled)
  }, [supported])

  const toggle = useCallback(async (next: boolean) => {
    if (!supported || busy) return
    setBusy(true)
    try {
      if (next) {
        const ok = await scheduleNudge()
        // Permission denied at the OS level. Reflect reality rather than
        // showing a switch that claims to be on while nothing is scheduled.
        setBlocked(!ok)
        setEnabled(ok)
      } else {
        await disableNudge()
        setBlocked(false)
        setEnabled(false)
      }
    } finally {
      setBusy(false)
    }
  }, [supported, busy])

  return { supported, enabled, busy, blocked, toggle, hour: NUDGE_HOUR }
}

/**
 * Re-arms the schedule once per session, after sign-in.
 *
 * A reboot or an app update can clear pending notifications on some Android
 * builds, so scheduling only at first run would quietly stop working after a
 * few weeks.
 */
export function useNudgeSync(signedIn: boolean) {
  useEffect(() => {
    if (!signedIn || !nudgeSupported()) return
    void syncNudge()
  }, [signedIn])
}
